<template>
  <div class="fspec" ref="rootRef">
    <button
      type="button"
      class="fspec__trigger"
      :class="{ 'is-open': open }"
      :aria-expanded="open"
      aria-controls="manage-feature-spec"
      data-tour="manage-feature-search"
      title="이 화면 도움말 열기"
      @click="togglePanel"
    >
      <AppIcon icon="mdi-help-circle-outline" :size="18" />
      <span class="fspec__trigger-text">
        <strong>도움말</strong>
        <em>{{ triggerLabel }}</em>
      </span>
    </button>

    <transition name="fspec-pop">
      <aside
        v-if="open"
        id="manage-feature-spec"
        class="fspec__panel"
        :class="{ 'is-searching': showSearchView }"
        role="dialog"
        aria-modal="true"
        aria-label="화면 도움말"
        @keydown="onPanelKeydown"
      >
        <header class="fspec__head">
          <div>
            <p class="fspec__eyebrow">화면 도움말</p>
            <h2>{{ panelTitle }}</h2>
          </div>
          <button type="button" class="fspec__close" aria-label="닫기" @click="closePanel">
            <AppIcon icon="mdi-close" :size="18" />
          </button>
        </header>

        <label class="fspec__search">
          <AppIcon icon="mdi-magnify" :size="18" />
          <input
            ref="searchInputRef"
            v-model.trim="query"
            type="search"
            placeholder="예: 지급일, 클레임, 송장"
            autocomplete="off"
            aria-label="기능 검색"
            @keydown="onSearchKeydown"
          />
          <button
            v-if="query"
            type="button"
            class="fspec__search-clear"
            aria-label="검색어 지우기"
            @click="clearQuery"
          >
            <AppIcon icon="mdi-close-circle" :size="16" />
          </button>
        </label>

        <template v-if="showSearchView">
          <p class="fspec__search-meta">
            <template v-if="isSearching && hits.length">
              {{ audienceLabel }} 기능 {{ hits.length }}건
            </template>
            <template v-else-if="isSearching">「{{ query }}」와 맞는 기능이 없습니다.</template>
            <template v-else>아래에서 찾는 말을 누르거나, 위에 검색어를 입력하세요.</template>
          </p>

          <section v-if="!isSearching" class="fspec__section">
            <h3>자주 찾는 말</h3>
            <div class="fspec__chips">
              <button
                v-for="item in suggestedQueries"
                :key="item"
                type="button"
                class="fspec__chip"
                @click="query = item"
              >
                {{ item }}
              </button>
            </div>
          </section>

          <section v-if="recentQueries.length && !hits.length" class="fspec__section">
            <h3>최근 검색</h3>
            <div class="fspec__chips">
              <button
                v-for="item in recentQueries"
                :key="item"
                type="button"
                class="fspec__chip"
                @click="query = item"
              >
                {{ item }}
              </button>
            </div>
          </section>

          <ul v-if="hits.length" class="fspec__hits" role="listbox">
            <li
              v-for="(hit, index) in hits"
              :key="`${hit.kind}-${hit.routeName}-${hit.title}-${index}`"
            >
              <button
                type="button"
                class="fspec__hit"
                :class="{ 'is-active': index === activeHit }"
                role="option"
                :aria-selected="index === activeHit"
                @mouseenter="activeHit = index"
                @click="openHit(hit)"
              >
                <span class="fspec__hit-top">
                  <em class="fspec__hit-kind" :data-kind="hit.kind">{{ hit.matchedIn }}</em>
                  <span class="fspec__hit-page">{{ hit.pageTitle }}</span>
                </span>
                <strong>{{ hit.title }}</strong>
                <span class="fspec__hit-snippet">{{ hit.snippet }}</span>
              </button>
            </li>
          </ul>

          <p class="fspec__hint">결과를 누르면 해당 화면으로 이동하고, 위치를 표시합니다.</p>
        </template>

        <template v-else-if="spec">
          <p class="fspec__summary">{{ spec.summary }}</p>

          <button type="button" class="fspec__tour-btn" @click="onStartTour">
            <AppIcon icon="mdi-play-circle-outline" :size="18" />
            화면에서 순서대로 보기
          </button>

          <section v-if="suggestedQueries.length" class="fspec__section">
            <h3>다른 화면 찾기</h3>
            <div class="fspec__chips">
              <button
                v-for="item in suggestedQueries.slice(0, 6)"
                :key="item"
                type="button"
                class="fspec__chip"
                @click="query = item"
              >
                {{ item }}
              </button>
            </div>
          </section>

          <section v-if="recentQueries.length" class="fspec__section">
            <h3>최근 검색</h3>
            <div class="fspec__chips">
              <button
                v-for="item in recentQueries"
                :key="item"
                type="button"
                class="fspec__chip"
                @click="query = item"
              >
                {{ item }}
              </button>
            </div>
          </section>

          <section class="fspec__section">
            <h3>이 화면에서 하는 일</h3>
            <p>{{ spec.purpose }}</p>
          </section>

          <section class="fspec__section">
            <h3>기능 목록</h3>
            <p class="fspec__section-hint">항목을 누르면 화면에서 위치를 보여 줍니다.</p>
            <ul>
              <li v-for="item in visibleFeatures" :key="item.name">
                <button
                  v-if="item.target"
                  type="button"
                  class="fspec__feature-btn"
                  @click="onHighlightFeature(item)"
                >
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.description }}</span>
                </button>
                <template v-else>
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.description }}</span>
                </template>
              </li>
            </ul>
          </section>

          <section v-if="spec.notes?.length" class="fspec__section">
            <h3>참고</h3>
            <ul class="fspec__notes">
              <li v-for="note in spec.notes" :key="note">{{ note }}</li>
            </ul>
          </section>
        </template>
      </aside>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  featureSpecOf,
  resolveFeatureSpecNavigation,
  searchFeatureSpecs,
  type FeatureSearchHit,
  type FeatureSpecItem,
} from '@/constants/manageFeatureSpecs'
import { useFeatureTour } from '@/composables/useFeatureTour'

const RECENT_KEY = 'hamuri.featureSpec.recentQueries'
const MAX_RECENT = 6

const SELLER_SUGGESTS = ['송장', '발송', '클레임', '재고', '정산', '후기', '계좌']
const ADMIN_SUGGESTS = ['입금확인', '검수', '클레임', '정산', '공지', 'FAQ', '환불', '특가']

const route = useRoute()
const router = useRouter()
const open = ref(false)
const query = ref('')
const activeHit = ref(0)
const rootRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const recentQueries = ref<string[]>([])
const { startTour } = useFeatureTour()

const spec = computed(() => featureSpecOf(route.name))

const audience = computed<'seller' | 'admin'>(() => {
  if (spec.value?.audience) return spec.value.audience
  const name = String(route.name || '')
  if (name.startsWith('admin') || String(route.path || '').startsWith('/admin')) return 'admin'
  return 'seller'
})

const audienceLabel = computed(() => (audience.value === 'admin' ? '관리자' : '판매'))
const triggerLabel = computed(() => spec.value?.title || '화면 안내')
const isSearching = computed(() => query.value.length >= 1)
const showSearchView = computed(() => isSearching.value || !spec.value)
const panelTitle = computed(() => {
  if (isSearching.value || !spec.value) return '기능 찾기'
  return spec.value.title
})

const suggestedQueries = computed(() =>
  audience.value === 'admin' ? ADMIN_SUGGESTS : SELLER_SUGGESTS,
)

/** 도움말 버튼 자체 설명은 목록에서 빼서 중복을 줄인다 */
const visibleFeatures = computed(() =>
  (spec.value?.features || []).filter((item) => item.target !== 'manage-feature-search'),
)

const hits = computed(() =>
  searchFeatureSpecs(query.value, { audience: audience.value, limit: 20 }),
)

watch(
  () => route.name,
  () => {
    open.value = false
    query.value = ''
    activeHit.value = 0
  },
)

watch(hits, () => {
  activeHit.value = 0
})

watch(open, async (value) => {
  if (!value) return
  await nextTick()
  /* 모바일·마우스 사용자를 위해 자동 포커스는 데스크톱에서만 */
  if (window.matchMedia('(pointer: fine)').matches) {
    searchInputRef.value?.focus()
  }
})

function loadRecent() {
  try {
    const raw = sessionStorage.getItem(RECENT_KEY)
    const list = raw ? (JSON.parse(raw) as string[]) : []
    recentQueries.value = Array.isArray(list) ? list.filter((item) => typeof item === 'string') : []
  } catch {
    recentQueries.value = []
  }
}

function pushRecent(value: string) {
  const next = value.trim()
  if (next.length < 1) return
  const list = [next, ...recentQueries.value.filter((item) => item !== next)].slice(0, MAX_RECENT)
  recentQueries.value = list
  try {
    sessionStorage.setItem(RECENT_KEY, JSON.stringify(list))
  } catch {
    /* ignore quota */
  }
}

function togglePanel() {
  open.value = !open.value
  if (!open.value) {
    query.value = ''
    activeHit.value = 0
  }
}

function closePanel() {
  open.value = false
  query.value = ''
  activeHit.value = 0
}

function clearQuery() {
  query.value = ''
  activeHit.value = 0
  nextTick(() => searchInputRef.value?.focus())
}

function onStartTour() {
  if (!spec.value) return
  open.value = false
  window.setTimeout(() => {
    if (spec.value) startTour(spec.value)
  }, 80)
}

function onHighlightFeature(item: FeatureSpecItem) {
  if (!spec.value) return
  open.value = false
  window.setTimeout(() => {
    if (spec.value) {
      startTour(spec.value, { featureName: item.name, target: item.target })
    }
  }, 80)
}

async function openHit(hit: FeatureSearchHit) {
  pushRecent(query.value)
  open.value = false
  query.value = ''
  activeHit.value = 0

  const nav = resolveFeatureSpecNavigation(hit.routeName)
  const destName = nav.name
  const remapped = destName !== hit.routeName
  const queryMatches =
    !nav.query ||
    Object.entries(nav.query).every(([key, value]) => String(route.query[key] ?? '') === value)
  const alreadyThere = String(route.name || '') === destName && queryMatches

  try {
    if (!alreadyThere) {
      await router.push(nav.query ? { name: destName, query: nav.query } : { name: destName })
    }
  } catch {
    /* 중복 이동·필수 파라미터·가드 실패 시에도 현재 화면 안내로 이어간다 */
  }

  await nextTick()
  const delay = alreadyThere ? 100 : 420
  window.setTimeout(() => {
    const pageSpec =
      featureSpecOf(remapped ? destName : hit.routeName) || featureSpecOf(hit.routeName)
    if (!pageSpec) {
      open.value = true
      return
    }

    if (hit.kind === 'feature' && (hit.featureName || hit.target)) {
      const targetReady =
        Boolean(hit.target) &&
        Boolean(document.querySelector(`[data-tour="${hit.target}"]`))
      const nameOnPage = Boolean(
        hit.featureName && pageSpec.features.some((item) => item.name === hit.featureName),
      )

      if (!remapped || targetReady || nameOnPage) {
        startTour(pageSpec, { featureName: hit.featureName, target: hit.target })
        return
      }

      /* 편집 화면 등 목록으로 치환된 경우: 대상 UI가 없으면 도움말만 연다 */
      open.value = true
      return
    }

    open.value = true
  }, delay)
}

function onSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    if (!hits.value.length) return
    event.preventDefault()
    activeHit.value = (activeHit.value + 1) % hits.value.length
    return
  }
  if (event.key === 'ArrowUp') {
    if (!hits.value.length) return
    event.preventDefault()
    activeHit.value = (activeHit.value - 1 + hits.value.length) % hits.value.length
    return
  }
  if (event.key === 'Enter') {
    const hit = hits.value[activeHit.value]
    if (hit) {
      event.preventDefault()
      void openHit(hit)
    }
    return
  }
  if (event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    if (query.value) {
      clearQuery()
    } else {
      closePanel()
    }
  }
}

function focusableInPanel(): HTMLElement[] {
  const panel = rootRef.value?.querySelector('#manage-feature-spec')
  if (!panel) return []
  return [
    ...panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ),
  ].filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null)
}

function onPanelKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    if (query.value) clearQuery()
    else closePanel()
    return
  }
  if (event.key !== 'Tab' || !open.value) return
  const nodes = focusableInPanel()
  if (nodes.length < 2) return
  const first = nodes[0]
  const last = nodes[nodes.length - 1]
  const active = document.activeElement as HTMLElement | null
  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

function onDocClick(event: MouseEvent) {
  if (!open.value || !rootRef.value) return
  if (!rootRef.value.contains(event.target as Node)) {
    closePanel()
  }
}

function onGlobalKeydown(event: KeyboardEvent) {
  if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'k') return
  const target = event.target as HTMLElement | null
  const tag = target?.tagName?.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
    /* 입력 중에는 브라우저 기본 동작을 방해하지 않는다. 단, 패널이 열려 있으면 포커스만 옮긴다 */
    if (!open.value) return
  }
  event.preventDefault()
  open.value = true
  nextTick(() => searchInputRef.value?.focus())
}

onMounted(() => {
  loadRecent()
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onGlobalKeydown)
})
</script>

<style lang="scss" src="./FeatureSpecPanel.scss"></style>
